import { useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import { FileText, FilePlus, Plus, ClockAlert, TriangleAlert } from '@/components/icons'
import { cn } from '@/lib/utils'
import { hapticLight } from '@/lib/haptics'
import { SearchInput } from './ui/search-input'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { DocumentFormDrawer } from './documents/DocumentFormDrawer'
import { ViewDocumentDrawer } from './documents/ViewDocumentDrawer'
import { TopBarBackIconButton } from './topbar/TopBarBackIconButton'
import { useDocumentSearchCompliance } from '../lib/hooks'
import { useIsAdmin } from '../hooks/useIsCompanyAdmin'
import { AppLayout } from './AppLayout'
import { DesktopPage } from './desktop/DesktopPage'
import { DesktopPageHeader } from './desktop/DesktopPageHeader'
import { useIsDesktop } from './ui/use-mobile'
import { EmptyState } from './EmptyState'
import { AlertBadge } from './ui/alert-badge'
import dayjs from 'dayjs'
import { Button } from './ui/button'

export function DocumentsPublic({
  initialDocumentId,
}: {
  initialDocumentId?: string
}) {
  const { isAdmin } = useIsAdmin()
  const isDesktop = useIsDesktop()
  const [searchQuery, setSearchQuery] = useState('')
  const [showAddDrawer, setShowAddDrawer] = useState(false)
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    initialDocumentId ?? null
  )
  const [selectedDocumentType, setSelectedDocumentType] = useState<
    string | null
  >(initialDocumentId ? 'compliance' : null)
  // Separate open flag; the selection is kept on close so the drawer can play
  // its exit animation (the next tap overwrites it).
  const [isViewDrawerOpen, setIsViewDrawerOpen] = useState(!!initialDocumentId)

  const { data: documents = [], isPending: loading } = useDocumentSearchCompliance(
    searchQuery || undefined
  )

  const isExpired = (expiryDate: Date | string | null | undefined): boolean => {
    if (!expiryDate) return false
    return dayjs(expiryDate).isBefore(dayjs())
  }

  const isExpiringSoon = (
    expiryDate: Date | string | null | undefined
  ): boolean => {
    if (!expiryDate) return false
    const expiry = dayjs(expiryDate)
    const now = dayjs()
    return expiry.isAfter(now) && expiry.diff(now, 'day') <= 30
  }

  const formatExpiryDate = (dateValue: Date | string) => {
    return dayjs(dateValue).format('MMM D, YYYY')
  }

  const getAlertVariant = (expiryDate: Date | string | null | undefined) => {
    if (isExpired(expiryDate)) return 'error' as const
    if (isExpiringSoon(expiryDate)) return 'warning' as const
    return null
  }

  // Calculate stats
  const totalDocs = documents.length
  const expiringDocs = documents.filter((doc) =>
    isExpiringSoon(doc.expiryDate)
  ).length
  const expiredDocs = documents.filter((doc) => isExpired(doc.expiryDate)).length

  const searchInput = (
    <SearchInput
      placeholder="Search documents"
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      onClear={() => setSearchQuery('')}
    />
  )

  const statsBlock = loading ? (
    <div className="flex w-full gap-3">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-[88px] flex-1 rounded-xl" />
      ))}
    </div>
  ) : (
    <div className="flex w-full gap-3">
      <DocumentStatCard
        label="Total"
        value={totalDocs}
        caption="Documents"
        icon={<FileText />}
      />
      <DocumentStatCard
        label="Expiring"
        value={expiringDocs}
        caption="Next 30 days"
        icon={<ClockAlert />}
        accent={expiringDocs > 0 ? 'notice' : undefined}
      />
      <DocumentStatCard
        label="Expired"
        value={expiredDocs}
        caption="Need renewal"
        icon={<TriangleAlert />}
        accent={expiredDocs > 0 ? 'negative' : undefined}
      />
    </div>
  )

  const documentList = (
    <div className="w-full flex flex-col gap-3">
      {loading ? (
        Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="w-full p-4">
            <div className="flex items-center gap-4">
              <Skeleton className="size-6 shrink-0 rounded-md" />
              <div className="flex flex-col gap-2 min-w-0 flex-1">
                <Skeleton className="h-5 w-1/2" />
                <div className="flex items-center gap-3">
                  <Skeleton className="h-6 w-20 rounded-lg" />
                  <Skeleton className="h-6 w-28 rounded-lg" />
                </div>
              </div>
            </div>
          </Card>
        ))
      ) : documents.length === 0 ? (
        <EmptyState
          icon={FilePlus}
          title="No documents yet"
          description="Upload manuals, procedures, or important files for quick access during operations."
          addButtonText={isAdmin ? 'Add Document' : undefined}
          onAdd={isAdmin ? () => setShowAddDrawer(true) : undefined}
        />
      ) : (
        documents.map((doc) => (
          <Card
            key={`${doc.type}-${doc.id}`}
            onClick={() => {
              if (doc.type === 'compliance') {
                setSelectedDocumentId(doc.id)
                setSelectedDocumentType('compliance')
                setIsViewDrawerOpen(true)
              }
            }}
            className="w-full p-4 cursor-pointer active:bg-surface-L2/50 transition-colors"
          >
            <div className="flex items-center justify-between gap-4">
              {/* Left: icon + info */}
              <div className="flex items-center gap-4 min-w-0">
                <FileText className="size-6 text-content-brand-bold shrink-0" />
                <div className="flex flex-col gap-2 min-w-0">
                  <p className="text-base font-semibold text-content-primary-bold tracking-[-0.5px] truncate">
                    {doc.title}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center h-6 px-2 rounded-lg border border-border-L1 bg-surface-L0 text-xs font-semibold text-content-primary-subtle">
                      {doc.documentType}
                    </span>
                    {doc.expiryDate && (
                      <span className="inline-flex items-center h-6 px-2 rounded-lg border border-border-L1 bg-surface-L0 text-xs font-semibold text-content-primary-subtle">
                        Expires {formatExpiryDate(doc.expiryDate)}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Status badge — only shown when expired or expiring soon */}
              {getAlertVariant(doc.expiryDate) && (
                <AlertBadge variant={getAlertVariant(doc.expiryDate)!} />
              )}
            </div>
          </Card>
        ))
      )}
    </div>
  )

  const drawers = (
    <>
      {/* Upload Compliance Document Drawer (admin only) */}
      <DocumentFormDrawer
        isOpen={showAddDrawer}
        onClose={() => setShowAddDrawer(false)}
        owner={{ type: 'compliance' }}
      />

      {/* View Compliance Document Drawer */}
      <ViewDocumentDrawer
        isOpen={isViewDrawerOpen && selectedDocumentType === 'compliance'}
        onClose={() => setIsViewDrawerOpen(false)}
        documentId={selectedDocumentId}
      />
    </>
  )

  if (isDesktop) {
    return (
      <>
        {drawers}
        <DesktopPage>
          <DesktopPageHeader
            title="Documents"
            subtitle="Manuals, procedures, and compliance files"
            breadcrumb={
              <span>
                <Link to="/operations" className="hover:underline">
                  Operations
                </Link>
                <span className="text-content-primary-subtle"> / Documents</span>
              </span>
            }
            action={
              isAdmin ? (
                <Button
                  roundness="round"
                  variant="brand"
                  size="sm"
                  className="gap-2"
                  onClick={() => {
                    hapticLight()
                    setShowAddDrawer(true)
                  }}
                >
                  Add Document
                  <Plus className="size-4 text-content-primary-white" />
                </Button>
              ) : undefined
            }
          />
          {searchInput}
          {statsBlock}
          {documentList}
        </DesktopPage>
      </>
    )
  }

  return (
    <AppLayout
      topBar={
        <TopBarBackIconButton
          title="Documents"
          rightElement={
            isAdmin ? (
              <Button
                roundness="round"
                variant="brand"
                size="sm"
                className="size-8 p-0"
                onClick={() => {
                  setShowAddDrawer(true)
                  hapticLight()
                }}
              >
                <Plus className="size-4 text-content-primary-white" />
              </Button>
            ) :
            undefined
          }
        />
      }
      className="pb-4"
    >
      <div className="flex flex-col gap-4 pt-4">
        {/* Sticky search header: opaque band so the list scrolls beneath it */}
        <div className="sticky top-0 z-10 bg-surface-brand-L0 pb-2">
          {searchInput}
        </div>

        {statsBlock}
        {documentList}
      </div>

      {drawers}
    </AppLayout>
  )
}

type StatAccent = 'notice' | 'negative'

const accentStyles: Record<StatAccent, { border: string; icon: string }> = {
  notice: {
    border: 'border-content-notice-bold',
    icon: 'text-background-notice-bold',
  },
  negative: {
    border: 'border-content-negative-bold',
    icon: 'text-content-negative-bold',
  },
}

function DocumentStatCard({
  label,
  value,
  caption,
  icon,
  accent,
}: {
  label: string
  value: string | number
  caption: string
  icon: ReactNode
  accent?: StatAccent
}) {
  const accentStyle = accent ? accentStyles[accent] : null

  return (
    <div
      className={cn(
        'flex flex-1 min-w-0 flex-col items-start justify-center rounded-xl border border-transparent bg-surface-brand-L1 p-4',
        accentStyle?.border
      )}
    >
      <div className="flex w-full items-center justify-between">
        <p className="text-xs font-semibold uppercase leading-4 text-content-primary-subtle">
          {label}
        </p>
        <span
          className={cn(
            'flex shrink-0 [&>svg]:size-4',
            accentStyle?.icon ?? 'text-content-primary-subtle'
          )}
        >
          {icon}
        </span>
      </div>
      <p className="text-[22px] font-bold leading-7 tracking-[-1px] text-content-primary-bold">
        {value}
      </p>
      <p className="text-[10px] font-semibold leading-4 text-content-primary-subtle">
        {caption}
      </p>
    </div>
  )
}
